import React, { useState } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { ArrowRightLeft, X, AlertTriangle } from 'lucide-react';
import SeasonalSuggestion from './SeasonalSuggestion';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ChangeProgramDialog — Phase 2 supersede workflow.
 *
 * Presents the operator with:
 *   1. The new-program selection + cadence
 *   2. An explicit choice on what happens to the OLD enrollment's
 *      already-scheduled future visits (cancel vs keep) — required.
 *
 * Then POSTs to /api/leads/{leadId}/enrollments/{enrollmentId}/change-program.
 * Backend supersedes the old enrollment atomically and seeds Visit #1
 * on the new one. Historical completed Jobs remain tagged to the old.
 */
const ChangeProgramDialog = ({
  isOpen,
  currentEnrollment,
  client,
  programs,
  futureVisitCount,
  onClose,
  onChanged,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [servicePlanTemplateId, setServicePlanTemplateId] = useState('');
  const [programName, setProgramName] = useState('');
  const [interval, setInterval]   = useState(currentEnrollment?.interval ?? 14);
  const [unit, setUnit]           = useState(currentEnrollment?.unit || 'day');
  const [rollMode, setRollMode]   = useState(currentEnrollment?.rollMode || 'schedule');
  const [limited, setLimited]     = useState(false);
  const [totalOcc, setTotalOcc]   = useState(12);
  const [startDate, setStartDate] = useState('');
  const [pricingAmount, setPricingAmount] = useState('');
  const [cancelChoice, setCancelChoice] = useState(null); // 'cancel' | 'keep' | null

  if (!isOpen) return null;

  const selectedProgram = programs?.find((p) => p.id === servicePlanTemplateId);
  const oldTitle = currentEnrollment?.programName || currentEnrollment?.planLabel || 'current program';

  const canSubmit = () => {
    const iv = parseInt(interval, 10);
    if (!Number.isFinite(iv) || iv < 1) return false;
    if (limited) {
      const t = parseInt(totalOcc, 10);
      if (!Number.isFinite(t) || t < 1) return false;
    }
    if (!programName && !selectedProgram) return false;
    if (futureVisitCount > 0 && cancelChoice == null) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;
    setSubmitting(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const body = {
        cancelFutureVisits: (futureVisitCount === 0) ? false : (cancelChoice === 'cancel'),
        reason: 'changed_program',
        servicePlanTemplateId: servicePlanTemplateId || null,
        programName: programName || selectedProgram?.name || null,
        interval: parseInt(interval, 10),
        unit,
        rollMode,
        totalOccurrences: limited ? parseInt(totalOcc, 10) : null,
        startDate: startDate || null,
        pricingAmount: pricingAmount === '' ? null : Number(pricingAmount),
      };
      await axios.post(
        `${API}/api/leads/${client.id}/enrollments/${currentEnrollment.enrollmentId}/change-program`,
        body,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success('Program changed · new enrollment started at Visit #1');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change program');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="change-program-dialog"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-gray-900">Change Program</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-md border border-indigo-100 bg-indigo-50/60 px-3 py-2">
            <p className="text-xs text-indigo-900 leading-snug">
              Ending <strong>{oldTitle}</strong> and starting a new program.
              Completed visits on the old program stay in history.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">New Program</label>
            <select
              value={servicePlanTemplateId}
              onChange={(e) => {
                const pid = e.target.value;
                setServicePlanTemplateId(pid);
                const p = programs?.find((x) => x.id === pid);
                if (p && !programName) setProgramName(p.name || '');
                if (p?.suggestedIntervalDays) setInterval(p.suggestedIntervalDays);
              }}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-emerald-400"
              data-testid="change-program-select"
            >
              <option value="">— None (custom) —</option>
              {programs?.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Program Label</label>
            <input
              type="text"
              value={programName}
              onChange={(e) => setProgramName(e.target.value)}
              placeholder="Program name / label"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-emerald-400"
              data-testid="change-program-name-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Cadence</label>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">Every</span>
                <input type="number" min="1" max="365" value={interval}
                       onChange={(e) => setInterval(e.target.value)}
                       className="w-14 text-sm border border-gray-200 rounded px-2 py-1 text-center"
                       data-testid="change-program-interval-input" />
                <select value={unit} onChange={(e) => setUnit(e.target.value)}
                        className="text-sm border border-gray-200 rounded px-1 py-1 bg-white"
                        data-testid="change-program-unit-select">
                  <option value="day">day{parseInt(interval, 10) === 1 ? '' : 's'}</option>
                  <option value="week">week{parseInt(interval, 10) === 1 ? '' : 's'}</option>
                  <option value="month">month{parseInt(interval, 10) === 1 ? '' : 's'}</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
              <input type="date" value={startDate}
                     onChange={(e) => setStartDate(e.target.value)}
                     className="w-full text-sm border border-gray-200 rounded px-2 py-1"
                     data-testid="change-program-start-date-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Occurrences</label>
              <div className="flex items-center gap-2">
                <label className="text-xs flex items-center gap-1">
                  <input type="radio" checked={!limited} onChange={() => setLimited(false)} />
                  Unlimited
                </label>
                <label className="text-xs flex items-center gap-1">
                  <input type="radio" checked={limited} onChange={() => setLimited(true)} />
                  Limit
                </label>
                {limited && (
                  <input type="number" min="1" value={totalOcc}
                         onChange={(e) => setTotalOcc(e.target.value)}
                         className="w-14 text-sm border border-gray-200 rounded px-2 py-1 text-center"
                         data-testid="change-program-total-input" />
                )}
              </div>
            </div>            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Price / visit</label>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                <input type="number" min="0" step="0.01" value={pricingAmount}
                       onChange={(e) => setPricingAmount(e.target.value)}
                       placeholder="0.00"
                       className="w-full text-sm border border-gray-200 rounded pl-5 pr-2 py-1"
                       data-testid="change-program-price-input" />
              </div>
            </div>
          </div>

          {/* Sprint 310 — restored seasonal suggestion for Change Program. */}
          <SeasonalSuggestion
            programs={programs}
            selectedProgramId={servicePlanTemplateId}
            startDate={startDate}
            intervalDays={interval}
            limited={limited}
            currentCount={totalOcc}
            onUseSuggestion={(n) => { setLimited(true); setTotalOcc(n); }}
          />

          {/* Operator choice on future visits — required when there are any */}
          {futureVisitCount > 0 && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50/60 p-3 space-y-2"
              data-testid="change-program-future-visits-choice"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-900 leading-snug">
                  <strong>{oldTitle}</strong> has {futureVisitCount} scheduled future{' '}
                  visit{futureVisitCount === 1 ? '' : 's'}. What should happen to{' '}
                  {futureVisitCount === 1 ? 'it' : 'them'}?
                </p>
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio"
                       checked={cancelChoice === 'cancel'}
                       onChange={() => setCancelChoice('cancel')}
                       data-testid="change-program-choice-cancel" />
                <div className="text-xs text-gray-800">
                  <div className="font-medium">Cancel future visits</div>
                  <div className="text-gray-500">Removes {futureVisitCount} scheduled visit{futureVisitCount === 1 ? '' : 's'} from the calendar.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio"
                       checked={cancelChoice === 'keep'}
                       onChange={() => setCancelChoice('keep')}
                       data-testid="change-program-choice-keep" />
                <div className="text-xs text-gray-800">
                  <div className="font-medium">Keep existing scheduled visits</div>
                  <div className="text-gray-500">Leaves the calendar untouched. The new program begins at Visit #1 alongside.</div>
                </div>
              </label>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSubmit}
              disabled={!canSubmit() || submitting}
              className="flex-1 text-sm font-medium py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              data-testid="change-program-submit"
            >
              {submitting ? 'Changing…' : 'Change program'}
            </button>
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeProgramDialog;
